import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PhotoDrop } from "./PhotoDrop";
import { http, errMsg } from "@/lib/api";
import { getVisitorId } from "@/lib/track";
import { SERVICES } from "@/data/services";
import { BUSINESS } from "@/config/businessConfig";

const EMPTY = { name: "", phone: "", email: "", town: "", service: "", preferred_date: "", contact_method: "phone", details: "" };

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block font-mono-x text-[12px] uppercase tracking-[0.14em] text-zinc-300 mb-2">{children}</label>
);

const Success = ({ onReset }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-10 border border-[var(--orange)] bg-[var(--orange)]/5 text-center" data-testid="quote-success">
    <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--orange)]" />
    <h3 className="mt-5 font-display font-black uppercase text-4xl">Request received.</h3>
    <p className="mt-3 text-zinc-300 max-w-md mx-auto">Thanks! We'll review your job details and get back to you with a quote shortly.</p>
    <button onClick={onReset} data-testid="quote-new-request-btn" className="btn-ghost mt-7 px-6 py-3">Send another request</button>
  </motion.div>
);

export const QuoteForm = () => {
  const [params] = useSearchParams();
  const pre = params.get("service");
  const initialService = pre === "other" ? "Other" : SERVICES.find((s) => s.id === pre)?.title || "";
  const [form, setForm] = useState({ ...EMPTY, service: initialService });
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append("photos", f));
    fd.append("visitor_id", getVisitorId());
    try {
      await http.post("/quotes", fd);
      setDone(true);
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (done) return <Success onReset={() => setDone(false)} />;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><Label htmlFor="q-name">Full name *</Label><input id="q-name" required minLength={2} className="field" value={form.name} onChange={set("name")} placeholder="Jane Smith" data-testid="quote-name-input" /></div>
        <div><Label htmlFor="q-phone">Phone *</Label><input id="q-phone" required type="tel" minLength={7} className="field" value={form.phone} onChange={set("phone")} placeholder="519-555-0000" data-testid="quote-phone-input" /></div>
        <div><Label htmlFor="q-email">Email *</Label><input id="q-email" required type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@email.com" data-testid="quote-email-input" /></div>
        <div>
          <Label htmlFor="q-town">Town</Label>
          <select id="q-town" className="field" value={form.town} onChange={set("town")} data-testid="quote-town-select">
            <option value="">Select your town</option>
            {BUSINESS.serviceTowns.map((t) => <option key={t}>{t}</option>)}
            <option>Other (Oxford County)</option>
          </select>
        </div>
        <div>
          <Label htmlFor="q-service">Type of job</Label>
          <select id="q-service" className="field" value={form.service} onChange={set("service")} data-testid="quote-service-select">
            <option value="">Select a service</option>
            {SERVICES.map((s) => <option key={s.id}>{s.title}</option>)}
            <option>Other</option>
          </select>
        </div>
        <div><Label htmlFor="q-date">Preferred date</Label><input id="q-date" type="date" className="field" value={form.preferred_date} onChange={set("preferred_date")} data-testid="quote-date-input" /></div>
      </div>
      <div>
        <Label>Best way to reach you</Label>
        <div className="grid grid-cols-3 gap-2" role="radiogroup">
          {["phone", "text", "email"].map((m) => (
            <button type="button" key={m} role="radio" aria-checked={form.contact_method === m} onClick={() => setForm((f) => ({ ...f, contact_method: m }))}
              data-testid={`contact-method-${m}`}
              className={`py-3 font-display text-lg font-bold uppercase border transition-colors duration-300 ${form.contact_method === m ? "bg-[var(--orange)] text-[var(--ink)] border-[var(--orange)]" : "border-[var(--line)] text-zinc-300 hover:border-zinc-500"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label htmlFor="q-details">Job details *</Label>
        <textarea id="q-details" required minLength={5} rows={5} className="field resize-y" value={form.details} onChange={set("details")} data-testid="quote-details-input"
          placeholder="What did you buy? Brand/model, how many items, where it's going (indoor/outdoor, wall type for mounting)..." />
      </div>
      <div><Label>Photos (optional)</Label><PhotoDrop files={files} setFiles={setFiles} /></div>
      <button type="submit" disabled={sending} data-testid="quote-submit-btn" className="btn-orange w-full justify-center py-5 text-xl disabled:opacity-70">
        {sending ? <span className="flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Sending...</span> : <><span>Request my free quote</span><ArrowUpRight className="w-5 h-5" /></>}
      </button>
      <p className="text-xs text-zinc-400">We'll only use your details to respond to your request.</p>
    </form>
  );
};
